import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { onPhoneRemote, phoneRemote, type PairRequest } from "./phoneRemote.js";

/**
 * "Allow Kim's iPhone to control this TV?" — pairing without typing a code
 * (owner, 2026-10-10). A phone on the Wi-Fi asks; whoever holds this TV's
 * remote says yes or no, which is what the code proved too.
 *
 * Starts on Allow, so it is one press of OK, but presses in the first moment
 * after it appears do not count: a press already on its way (someone flicking
 * through channels) cannot answer a question nobody has read. Back is "no",
 * which also stops that device asking again for a minute (native PairRequests).
 * Drawn on <body>, over the picture, like the remote's other messages.
 *
 * It also shows a code the phone can type instead (2026-10-19, roadmap Section 25b): a TV whose
 * remote is lost, or a projector remote that passes no OK, can still be paired from the phone.
 * Typing it closes this question ("pairRequestClosed").
 */
const SETTLE_MS = 800;

export function PairRequestDialog() {
  const [asked, setAsked] = useState<PairRequest | null>(null);
  const openedAt = useRef(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const allowRef = useRef<HTMLButtonElement>(null);

  useEffect(
    () =>
      onPhoneRemote("pairRequest", (request) => {
        openedAt.current = Date.now();
        setAsked(request);
      }),
    [],
  );

  // Paired by the code on the phone: the question is answered, so it goes.
  useEffect(
    () => onPhoneRemote("pairRequestClosed", ({ id }) => setAsked((current) => (current && current.id === id ? null : current))),
    [],
  );

  // The question lasts as long as the phone waits for it.
  useEffect(() => {
    if (!asked) return;
    const timer = window.setTimeout(() => setAsked(null), Math.max(1000, asked.leftMs));
    return () => window.clearTimeout(timer);
  }, [asked]);

  const answer = useCallback(
    (allow: boolean) => {
      if (!asked) return;
      if (Date.now() - openedAt.current < SETTLE_MS) return;
      void phoneRemote.answerPairRequest(asked.id, allow).catch(() => undefined);
      setAsked(null);
    },
    [asked],
  );

  const no = useCallback(() => {
    if (!asked) return;
    void phoneRemote.answerPairRequest(asked.id, false).catch(() => undefined);
    setAsked(null);
  }, [asked]);
  useDialogFocus({ open: asked !== null, onClose: no, rootRef: dialogRef });

  useEffect(() => {
    if (asked) allowRef.current?.focus();
  }, [asked]);

  if (!asked) return null;

  // Left/Right between the two buttons: a TV remote's D-pad.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const buttons = Array.from(dialogRef.current?.querySelectorAll("button") ?? []);
    if (buttons.length === 0) return;
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === "ArrowRight" ? 1 : -1;
    buttons[(Math.max(at, 0) + step + buttons.length) % buttons.length]?.focus();
    event.preventDefault();
  };

  return createPortal(
    <div className="epg-program-detail-overlay exit-confirm-overlay pair-request-overlay">
      <div
        ref={dialogRef}
        className="epg-program-detail exit-confirm pair-request"
        role="alertdialog"
        aria-labelledby="pair-request-title"
        aria-describedby="pair-request-desc"
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 className="epg-detail-title" id="pair-request-title">
          Allow “{asked.name}” to control this TV?
        </h2>
        <p className="pair-request__desc" id="pair-request-desc">
          It will work as a remote and second screen on this Wi-Fi. You can remove it any time in Settings → Phone remote.
        </p>
        {asked.code && (
          <p className="pair-request__code" aria-label={`Or type the code ${asked.code.split("").join(" ")} on the phone`}>
            No remote? Type <strong>{asked.code}</strong> on the phone.
          </p>
        )}
        <div className="exit-confirm__actions">
          <button ref={allowRef} type="button" className="exit-confirm__leave" onClick={() => answer(true)}>
            Allow
          </button>
          <button type="button" className="exit-confirm__stay" onClick={() => answer(false)}>
            Don&apos;t allow
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
